import { Clipboard } from '@angular/cdk/clipboard';
import { CommonModule } from '@angular/common';
import { Component, HostListener, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { SubLicenseManagementService } from '../../../sub-licenses/sub-license-management/sub-license-management.service';
import { event_status } from '../../../../../assets/constData/event_status';
import {
  buildEventDetailRouteSegments,
  resolveEventLandingTabId,
} from '../../../../utilities/event-landing-tab/event-landing-tab.util';
import { environment } from '../../../../../environments/environment';
import { CapitalizeTitlePipe } from '../../../../shared/pipes/capitalize-title.pipe';
import {
  EMPTY_ORGANISER_DETAILS,
  OrganiserDetails,
} from '../../../../models/event/organiser-details';

/** Visual tone of the list-view status pill; drives the `data-tone` styles. */
type StatusTone = 'registration' | 'upcoming' | 'ongoing' | 'complete' | 'canceled' | 'neutral' | 'draws_out';

// Keys are event_status.ts `class_name` values. There is no 'Completed'
// class_name in that list (dead key removed) — 'Recent' (value 15) is the
// completed/past state. Every class_name not listed here (Draws In Making,
// Draws Out, Rescheduled, Status 7, Pending, Verified, Rejected, Unverified,
// Incomplete) falls through to the 'neutral' tone below.
const STATUS_TONE_BY_LABEL: Readonly<Record<string, StatusTone>> = {
  'Registration Open': 'registration',
  'Registration Closed': 'registration',
  Upcoming: 'upcoming',
  Ongoing: 'ongoing',
  Recent: 'complete',
  Cancelled: 'canceled',
  'Draws Out': 'draws_out',
};

const padTwoDigits = (value: number): string => String(value).padStart(2, '0');

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

@Component({
  selector: 'stupa-event-list-card',
  standalone: true,
  imports: [CommonModule, TranslateModule, CapitalizeTitlePipe],
  templateUrl: './event-list-card.component.html',
  styleUrl: './event-list-card.component.scss',
})
export class EventListCardComponent implements OnInit, OnChanges, OnDestroy {

  constructor(
    private readonly router: Router,
    private readonly clipboard: Clipboard,
    private readonly snack_bar: MatSnackBar,
    private readonly subLicenseManagementService: SubLicenseManagementService,
  ) { }
  @Input() item: any;
  @Input() grid = false;
  @Input() is_sublicense = false;
  @Input() user_role_id: number | null = null;
  @Input() event_tab_index: number = 0;
  /** Organiser section of `item.extra_json`, parsed by the parent's `getOrganiserDetails()`. */
  @Input() organiserDetails: OrganiserDetails = EMPTY_ORGANISER_DETAILS;
  /**
   * Broadcast list view: hides the organiser/event level columns, swaps the
   * thumbnail column for a serial number (see `serialNo`), and reads the
   * status pill straight off `item.status` instead of resolving it through
   * `event_status`/`statusKey`.
   */
  @Input() isBroadcastRoute = false;
  /** Position of `item` within the parent's (cumulative, "load more") list — used to render `serialNo`. */
  @Input() rowIndex = 0;
  status = 'future';
  statusLabel = '';
  /** Translation key (`event_status.title`) for the status pill; `statusLabel` stays the tone-lookup key. */
  statusKey = '';
  statusTone: StatusTone = 'neutral';
  /** i18n key, e.g. 'league'/'tournament' — the template applies `| translate`. */
  eventTypeLabel = 'league';
  eventLevel = '--';
  dayCurrent = '00';
  dayTotal = '00';
  countdown = { days: '0', hours: '0', minutes: '0', seconds: '0' };
  stopCountdown?: () => void;

  is_copied: boolean = false;
  durationInSeconds = 3000;

  screenWidth: any;
  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.screenWidth = globalThis.window.innerWidth;
  }
  /** 1-based, zero-padded row position — broadcast list view's replacement for the thumbnail column. */
  get serialNo(): string {
    return padTwoDigits(this.rowIndex + 1);
  }

  status_color_map: { [key: string]: string } = {
    Ongoing: '#E30000',
    Completed: '#258C17',
    Upcoming: '#DAA21C',
    Cancelled: '#3B3B3B',
    'Registration Closed': '#E35B00',
    'Registration Open': '#E35B00',
    Recent: '#007BFF',
    'Draws In Making': '#9C27B0',
    'Draws Out': '#673AB7',
    Rescheduled: '#FF5722',
    'Status 7': '#795548',
    Pending: '#FFC107',
    Verified: '#4CAF50',
    Rejected: '#F44336',
    Unverified: '#9E9E9E',
    Incomplete: '#FF9800',
    // Universal fallback color for any unknown status
    default: '#607D8B',
  };

  ngOnInit() {
    this.screenWidth = globalThis.window.innerWidth;
    // Covers the case where `item` is assigned directly (e.g. in unit
    // tests) rather than through a template binding, which never fires
    // ngOnChanges. On normal template usage ngOnChanges (below) already
    // ran first with the same item, so this is a harmless no-op re-run.
    if (this.item) {
      this.refreshFromItem();
    }
  }

  /**
   * `all-events.component.html` tracks its `@for` loop by `item.id`
   * (`track item.id`), so when a refresh returns the same ids with updated
   * data, Angular reuses this component instance instead of recreating it —
   * `ngOnInit` never runs again. Recomputing everything derived from `item`
   * here too (not only in `ngOnInit`) keeps the row's status/date/level from
   * going stale after such a refresh.
   */
  ngOnChanges(changes: SimpleChanges) {
    if (!changes['item'] || !this.item) {
      return;
    }
    this.refreshFromItem();
  }

  private refreshFromItem() {
    // Stop the previous countdown before starting a new one — this instance
    // may be getting reused for a different item (see ngOnChanges above),
    // and leaving the old interval running would leak it and let it
    // overwrite `countdown` with the wrong event's values.
    this.stopCountdown?.();
    this.stopCountdown = this.startCountdown(
      this.item.event_start_date,
      (timeLeft) => {
        this.countdown = timeLeft;
      }
    );
    this.status = this.getEventStatus(
      this.item.event_start_date,
      this.item.event_end_date
    );
    const statusEntry = this.resolveEventStatusEntry();
    this.statusLabel = statusEntry?.class_name ?? '';
    this.statusKey = statusEntry?.title ?? '';
    this.statusTone = STATUS_TONE_BY_LABEL[this.statusLabel] ?? 'neutral';
    this.eventTypeLabel = this.item.event_type === 'T' ? 'tournament' : 'league';
    // An empty string from the API should also fall back to the placeholder.
    this.eventLevel = this.item.event_level || '--';
    this.dayCurrent = padTwoDigits(this.getDay(this.item.event_start_date));
    this.dayTotal = padTwoDigits(
      this.getDayDiff(this.item.event_start_date, this.item.event_end_date)
    );
  }

  /** Row-level keyboard activation; ignores keys bubbling up from the inner buttons. */
  onRowKeydown(event: Event) {
    if (event.target !== event.currentTarget) return;
    event.preventDefault();
    this.go_to_event(this.item);
  }

  /**
   * Shared handler for the row's trailing action button, whose label/target
   * depend on status: past and ongoing events already have results to show,
   * so they deep-link straight to the results tab ('view_result' ->
   * go_to_result, hardcoded to tab '5'/'5'); an upcoming event has no
   * results yet, so 'view_more' instead lands on the event's own landing
   * tab, same as clicking the row (go_to_event).
   */
  onViewResult(event: Event) {
    event.stopPropagation();
    if (this.status === 'future') {
      this.go_to_event(this.item);
    } else {
      this.go_to_result(this.item);
    }
  }

  source_event_id: any;
  go_to_event(data: any) {
    // Store source_event_id in session storage if available
    if (data?.source_event_id) {
      sessionStorage.setItem('source_event_id', data.source_event_id.toString());
    } else {
      // Clear session storage if no source_event_id (for regular events)
      sessionStorage.removeItem('source_event_id');
    }
    localStorage.setItem('event_type', data.event_type);
    if (this.is_sublicense && this.user_role_id != null) {
      this.subLicenseManagementService.setSubLicenseReturnContext({
        user_role_id: this.user_role_id,
        tab_index: 0,
        event_tab_index: this.event_tab_index,
      });
    }
    this.router.navigate(this.buildEventUrlCommands(data));
  }

  go_to_result(data: any) {
    if (this.is_sublicense && this.user_role_id != null) {
      this.subLicenseManagementService.setSubLicenseReturnContext({
        user_role_id: this.user_role_id,
        tab_index: 0,
        event_tab_index: this.event_tab_index,
      });
    }
    this.router.navigate(this.buildResultUrlCommands(data));
  }

  /** Router commands `go_to_event` navigates to — shared with `copyLink()` so Share always copies that same destination. */
  private buildEventUrlCommands(data: any): unknown[] {
    const currentRoute = this.is_sublicense ? 'events' : window.location.href.split('/')[3];
    const landingTabId = resolveEventLandingTabId(data.event_status, {
      tenantName: environment.tenant_Name,
      eventType: data.event_type,
    });
    return [currentRoute, ...buildEventDetailRouteSegments(data.id, landingTabId)];
  }

  /** Router commands `go_to_result` navigates to — shared with `copyLink()`. */
  private buildResultUrlCommands(data: any): unknown[] {
    const currentRoute = this.is_sublicense ? 'events' : globalThis.window.location.href.split('/')[3];
    return [currentRoute, data.id, 0, 0, '5', '5'];
  }

  /** Absolute URL `onViewResult` would navigate this row's click to, for the Share button to copy. */
  private getNavigationUrl(): string {
    const commands = this.status === 'future'
      ? this.buildEventUrlCommands(this.item)
      : this.buildResultUrlCommands(this.item);
    const path = this.router.serializeUrl(this.router.createUrlTree(commands));
    return `${globalThis.window.location.origin}${path}`;
  }

  getEventStatus(
    event_start_date: string,
    event_end_date: string
  ): 'past' | 'present' | 'future' {
    const today = new Date();
    const start = new Date(event_start_date);
    const end = new Date(event_end_date);

    if (today < start) return 'future';
    if (today > end) return 'past';
    return 'present'; // today is between start and end (inclusive)
  }

  startCountdown(
    targetDate: string,
    onTick: (timeLeft: {
      days: string;
      hours: string;
      minutes: string;
      seconds: string;
    }) => void
  ): () => void {
    const endTime = new Date(targetDate).getTime();

    const intervalId = setInterval(() => {
      const now = Date.now();
      const diff = endTime - now;

      if (diff <= 0) {
        clearInterval(intervalId);
        onTick({ days: '00', hours: '00', minutes: '00', seconds: '00' });
        return;
      }

      const days = String(Math.floor(diff / (1000 * 60 * 60 * 24))).padStart(
        2,
        '0'
      );
      const hours = String(Math.floor((diff / (1000 * 60 * 60)) % 24)).padStart(
        2,
        '0'
      );
      const minutes = String(Math.floor((diff / (1000 * 60)) % 60)).padStart(
        2,
        '0'
      );
      const seconds = String(Math.floor((diff / 1000) % 60)).padStart(2, '0');

      onTick({ days, hours, minutes, seconds });
    }, 1000);

    return () => clearInterval(intervalId); // cleanup function
  }

  getDay(startDate: any) {
    const start = new Date(startDate);
    const today = new Date();

    // Normalize both dates to midnight to avoid time differences
    start.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    const diffInMs = today.getTime() - start.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    return diffInDays + 1;
  }

  getDayDiff(startDate: any, endDate: any) {
    const start = new Date(startDate);
    const today = new Date(endDate);

    // Normalize both dates to midnight to avoid time differences
    start.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    const diffInMs = today.getTime() - start.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    return diffInDays + 1;
  }

  getdate(start_date: any, end_date: any) {
    // Check if start_date is valid before splitting
    const [startYear, startMonth, startDate] = start_date
      ? start_date.split('-')
      : [null, null, null];

    const [year, month, day] = end_date
      ? end_date.split('-')
      : [null, null, null];

    // Handle the case where the split might return null values
    if (startDate === null || day === null || startMonth === null || month === null) {
      return '';
    }

    const startMonthName = MONTHS[Number.parseInt(startMonth) - 1];
    const endMonthName = MONTHS[Number.parseInt(month) - 1];
    const startDay = Number.parseInt(startDate);
    const endDay = Number.parseInt(day);

    // Check if start and end months are different
    if (startMonth !== month || startYear !== year) {
      // Different months or years: show full date range with both months
      if (startYear === year) {
        // Same year, different months: "2nd January - 2nd February, 2029"
        return `${startDay}${this.getDaySuffix(startDay)} ${startMonthName} - ${endDay}${this.getDaySuffix(endDay)} ${endMonthName}, ${year}`;
      } else {
        // Different years: "2nd January, 2029 - 2nd February, 2030"
        return `${startDay}${this.getDaySuffix(startDay)} ${startMonthName}, ${startYear} - ${endDay}${this.getDaySuffix(endDay)} ${endMonthName}, ${year}`;
      }
    } else {
      // Same month and year: "2nd - 5th February, 2029"
      return `${startDay}${this.getDaySuffix(startDay)} - ${endDay}${this.getDaySuffix(endDay)} ${endMonthName}, ${year}`;
    }
  }

  /** Compact mobile date, e.g. "5 Jul 25 - 23 Aug 26" (desktop keeps `getdate`'s long form). */
  getMobileDate(start_date: any, end_date: any) {
    const [startYear, startMonth, startDate] = start_date
      ? start_date.split('-')
      : [null, null, null];

    const [year, month, day] = end_date
      ? end_date.split('-')
      : [null, null, null];

    if (startDate === null || day === null || startMonth === null || month === null) {
      return '';
    }

    // Optional chaining guards a malformed month (non-numeric, or outside
    // 1-12) the same way getdate()'s identical lookup degrades — printing
    // an empty month instead of throwing on .slice() of undefined.
    const startMonthShort = MONTHS[Number.parseInt(startMonth) - 1]?.slice(0, 3) ?? '';
    const endMonthShort = MONTHS[Number.parseInt(month) - 1]?.slice(0, 3) ?? '';
    const startDay = Number.parseInt(startDate);
    const endDay = Number.parseInt(day);
    const startYearShort = startYear?.slice(-2) ?? '';
    const endYearShort = year?.slice(-2) ?? '';

    if (startMonth === month && startYear === year) {
      return `${startDay} - ${endDay} ${endMonthShort} ${endYearShort}`;
    }
    return `${startDay} ${startMonthShort} ${startYearShort} - ${endDay} ${endMonthShort} ${endYearShort}`;
  }

  getDaySuffix(day: number) {
    if (day >= 11 && day <= 13) return 'th';
    switch (day % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  }

  copyLink(event: any) {
    event.stopPropagation();
    this.is_copied = true;
    this.clipboard.copy(this.getNavigationUrl());
    const sb = this.snack_bar.open('Link Copied', 'close', {
      duration: this.durationInSeconds,
      panelClass: ['success'],
    });
    sb.onAction().subscribe(() => {
      sb.dismiss();
    });
  }

  setEventStatus(status: number): any {
    return event_status.find((x: any) => x.value == status)?.class_name;
  }

  /**
   * `event_status` is keyed by numeric id either way — broadcast items just
   * carry that same numeric id under `item.status` instead of
   * `item.event_status` (matching `get_broadcasts`'s `status` query param).
   * Looking both up against the same table keeps statusLabel/statusTone/
   * statusKey (and so the countdown/progress timer, which key off tone)
   * working the same way regardless of route.
   */
  private resolveEventStatusEntry(): (typeof event_status)[number] | undefined {
    const value = this.isBroadcastRoute ? this.item?.status : this.item?.event_status;
    return event_status.find((x) => Number(x.value) === Number(value));
  }

  getStatusColor(statusName: string): string {
    return this.status_color_map[statusName] || this.status_color_map['default'];
  }

  ngOnDestroy() {
    if (this.stopCountdown) this.stopCountdown();
  }
}
